import { Aperture, Check, CheckSquare, Download, Images, KeyRound, LoaderCircle, LockKeyhole, Plus, Search, Settings2, Sparkles, Trash2, X } from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { AdminPanel } from "./components/AdminPanel";
import { Lightbox } from "./components/Lightbox";
import { MediaCard } from "./components/MediaCard";
import { UnicornHero } from "./components/UnicornHero";
import { api } from "./lib/api";
import type { MediaItem, MediaKind } from "./types";

type Filter = "all" | MediaKind;

export default function App() {
  const mobilePlatform = useMemo(() => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ios";
    if (/Android/i.test(ua)) return "android";
    return "desktop";
  }, []);
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [adminOpen, setAdminOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [lightbox, setLightbox] = useState<MediaItem | null>(null);
  const [savingAlbum, setSavingAlbum] = useState(false);
  const [preparedFiles, setPreparedFiles] = useState<File[] | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [deletingMany, setDeletingMany] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: "success" | "error" } | null>(null);

  const loadItems = useCallback(async () => {
    try {
      setItems(await api.getItems());
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "图库读取失败", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadItems();
    api.getSession().then((session) => {
      setAdmin(session.admin);
      setConfigured(session.configured);
    }).catch(() => undefined);
  }, [loadItems]);

  const pushToast = (message: string, tone: "success" | "error" = "success") => {
    setToast({ message, tone });
    window.setTimeout(() => setToast((current) => current?.message === message ? null : current), 3600);
  };

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesFilter = filter === "all" || item.kind === filter;
      const matchesSearch = !needle || `${item.title} ${item.caption} ${item.sourceAuthor ?? ""}`.toLowerCase().includes(needle);
      return matchesFilter && matchesSearch;
    });
  }, [filter, items, search]);

  const login = async (event: FormEvent) => {
    event.preventDefault();
    setLoginBusy(true);
    try {
      await api.login(password);
      setAdmin(true);
      setPassword("");
      setLoginOpen(false);
      setAdminOpen(true);
      pushToast("已进入管理模式");
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "登录失败", "error");
    } finally {
      setLoginBusy(false);
    }
  };

  const openAdmin = () => {
    if (admin) setAdminOpen(true);
    else setLoginOpen(true);
  };

  const logout = async () => {
    await api.logout().catch(() => undefined);
    setAdmin(false);
    setAdminOpen(false);
    setSelectionMode(false);
    setSelectedIds(new Set());
    pushToast("已退出管理模式");
  };

  const toggleSelectionMode = () => {
    setSelectionMode((current) => !current);
    setSelectedIds(new Set());
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    const visibleIds = filteredItems.map((item) => item.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds((current) => {
      const next = new Set(current);
      visibleIds.forEach((id) => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const bulkDelete = async (all: boolean) => {
    const count = all ? items.length : selectedIds.size;
    if (!count) return pushToast("先选择要删除的收藏", "error");
    const message = all
      ? `确定永久删除图库里的全部 ${count} 份收藏和原文件？此操作不可恢复。`
      : `确定永久删除选中的 ${count} 份收藏和原文件？`;
    if (!window.confirm(message)) return;
    setDeletingMany(true);
    try {
      const deleted = await api.deleteItems(all ? { all: true } : { ids: [...selectedIds] });
      setSelectedIds(new Set());
      setSelectionMode(false);
      setLightbox(null);
      await loadItems();
      pushToast(`已删除 ${deleted} 份收藏`);
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "批量删除失败", "error");
    } finally {
      setDeletingMany(false);
    }
  };

  const deleteItem = async (item: MediaItem) => {
    if (!window.confirm(`删除「${item.title}」及其归档文件？`)) return;
    try {
      await api.deleteItem(item.id);
      await loadItems();
      pushToast("已删除");
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "删除失败", "error");
    }
  };

  const openAlbumShare = async (files: File[]) => {
    if (mobilePlatform === "desktop") throw new Error("请在 iPhone Safari 或安卓 Chrome 中打开网站后保存到相册");
    if (!navigator.share || !navigator.canShare?.({ files })) throw new Error("当前浏览器不支持批量写入相册，请用手机 Safari 或 Chrome 打开");
    await navigator.share({
      files,
      title: "拾光册",
      text: mobilePlatform === "ios" ? "请在下方选择“存储图像”" : "请选择“相册/图库”保存这些图片",
    });
  };

  const saveToAlbum = async () => {
    if (preparedFiles?.length) {
      try {
        await openAlbumShare(preparedFiles);
        setPreparedFiles(null);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        pushToast(error instanceof Error ? error.message : "系统相册未打开", "error");
      }
      return;
    }
    const assets = filteredItems.flatMap((item) => item.assets).filter((asset) => asset.mimeType.startsWith("image/"));
    if (!assets.length) return pushToast("当前分类里没有图片", "error");
    setSavingAlbum(true);
    try {
      const files: File[] = [];
      const used = new Map<string, number>();
      let cursor = 0;
      const worker = async () => {
        while (cursor < assets.length) {
          const asset = assets[cursor++];
          const response = await fetch(asset.url);
          if (!response.ok) continue;
          const blob = await response.blob();
          const count = used.get(asset.fileName) ?? 0;
          used.set(asset.fileName, count + 1);
          const dot = asset.fileName.lastIndexOf(".");
          const name = count === 0 ? asset.fileName : dot > 0 ? `${asset.fileName.slice(0, dot)}-${count + 1}${asset.fileName.slice(dot)}` : `${asset.fileName}-${count + 1}`;
          files.push(new File([blob], name, { type: blob.type || asset.mimeType }));
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, assets.length) }, () => worker()));
      if (!files.length) throw new Error("图片读取失败");
      try {
        await openAlbumShare(files);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setPreparedFiles(files);
        pushToast("图片已准备好，请再点一次“保存到相册”打开系统菜单", "error");
      }
    } catch (error) {
      pushToast(error instanceof Error ? error.message : "准备图片失败", "error");
    } finally {
      setSavingAlbum(false);
    }
  };

  const counts: Record<Filter, number> = {
    all: items.length,
    upload: items.filter((item) => item.kind === "upload").length,
    douyin: items.filter((item) => item.kind === "douyin").length,
    xiaohongshu: items.filter((item) => item.kind === "xiaohongshu").length,
  };

  return (
    <div className="app-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="拾光册首页"><span><Aperture /></span><strong>拾光册</strong></a>
        <nav>
          <a href="#gallery">收藏</a>
          <a href="#about">关于</a>
          <button onClick={openAdmin}>{admin ? <Settings2 size={16} /> : <LockKeyhole size={16} />}{admin ? "管理" : "主人入口"}</button>
        </nav>
      </header>

      <main>
        <section className="hero" id="top">
          <UnicornHero />
          <div className="hero-layout">
            <div className="hero-content">
              <div className="eyebrow"><Sparkles size={15} /> PRIVATE MEDIA SPACE</div>
              <h1>图片都在这里。<br /><em>打开，选择，存相册。</em></h1>
              <p>一次上传 300 张。iPhone 和安卓都能批量调起系统相册，不用来回点每一张。</p>
              <div className="hero-actions">
                <button className="hero-primary" onClick={openAdmin}><Plus /><span>上传图片</span></button>
                <a href="#gallery">直接看图库</a>
              </div>
            </div>
            <aside className="hero-status">
              <div><strong>{items.length}</strong><span>当前收藏</span></div>
              <div><strong>300</strong><span>单次上传</span></div>
              <div><strong>R2</strong><span>云端原文件</span></div>
            </aside>
          </div>
        </section>

        <section className="gallery-section" id="gallery">
          <div className="section-heading">
            <div><span className="kicker">THE COLLECTION · {String(items.length).padStart(2, "0")}</span><h2>最近拾到的光</h2></div>
            <div className="search-box"><Search size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索标题或作者" aria-label="搜索收藏" />{search && <button onClick={() => setSearch("")} aria-label="清空搜索"><X size={15} /></button>}</div>
          </div>

          <div className="gallery-toolbar">
            <div className="filter-row" role="tablist" aria-label="收藏类型">
              {([
                ["all", "全部"], ["upload", "我的上传"], ["douyin", "抖音"], ["xiaohongshu", "小红书"],
              ] as const).map(([value, label]) => (
                <button key={value} className={filter === value ? "active" : ""} onClick={() => { setFilter(value); setPreparedFiles(null); }} role="tab" aria-selected={filter === value}>{label}<span>{counts[value]}</span></button>
              ))}
            </div>
            <div className="gallery-actions">
              {admin && !selectionMode && <>
                <button className="manage-selection-button" onClick={toggleSelectionMode} disabled={!items.length}><CheckSquare />选择删除</button>
                <button className="delete-all-button" onClick={() => void bulkDelete(true)} disabled={deletingMany || !items.length}><Trash2 />一键删除全部</button>
              </>}
              <button className={`save-album-button ${preparedFiles ? "ready" : ""}`} onClick={saveToAlbum} disabled={savingAlbum || !filteredItems.length}>
                {savingAlbum ? <LoaderCircle className="spin" /> : preparedFiles ? <Download /> : <Images />}
                {savingAlbum ? "正在准备图片…" : preparedFiles ? "再次点击保存" : mobilePlatform === "ios" ? "保存到 iPhone 相册" : mobilePlatform === "android" ? "保存到安卓相册" : "一键保存到手机相册"}
              </button>
            </div>
            {!selectionMode && <span className="album-hint">{mobilePlatform === "ios" ? "系统菜单中选择“存储图像”" : mobilePlatform === "android" ? "系统菜单中选择相册或图库" : "支持 iOS Safari 与 Android Chrome"}</span>}
          </div>

          {admin && selectionMode && (
            <div className="selection-bar">
              <div><strong>已选 {selectedIds.size} 项</strong><span>点图片即可选择或取消</span></div>
              <div>
                <button onClick={toggleAllFiltered}>{filteredItems.length > 0 && filteredItems.every((item) => selectedIds.has(item.id)) ? "取消全选" : `全选当前 ${filteredItems.length} 项`}</button>
                <button onClick={toggleSelectionMode}>取消</button>
                <button className="selection-delete" onClick={() => void bulkDelete(false)} disabled={deletingMany || !selectedIds.size}>
                  {deletingMany ? <LoaderCircle className="spin" /> : <Trash2 />}删除选中
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="state-panel"><LoaderCircle className="spin" /><p>正在把光影摆好…</p></div>
          ) : filteredItems.length ? (
            <div className="masonry-grid">
              {filteredItems.map((item) => <MediaCard key={item.id} item={item} admin={admin} selectionMode={selectionMode} selected={selectedIds.has(item.id)} onOpen={setLightbox} onDelete={deleteItem} onToggleSelect={toggleSelected} />)}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-sun"><Aperture /></div>
              <span>YOUR FIRST MEMORY</span>
              <h3>{items.length ? "没有找到匹配的收藏" : "这里还在等第一束光"}</h3>
              <p>{items.length ? "换个关键词或分类看看。" : "进入管理模式，上传照片或粘贴一条分享链接。"}</p>
              {!items.length && <button onClick={openAdmin}>添加第一份收藏</button>}
            </div>
          )}
        </section>

        <section className="about-section" id="about">
          <div><span className="kicker">ABOUT THIS SPACE</span><h2>简单一点，<br />才真的会每天用。</h2></div>
          <div className="about-copy"><p>原文件放在 Cloudflare R2，索引存在 D1。页面优先照顾手机拇指操作，上传、筛选、保存都在两步内完成。</p><p>抖音和小红书的公开分享链接直接贴进管理面板；解析到的图片或视频会自动归档。</p></div>
        </section>
      </main>

      <footer><a className="brand" href="#top"><span><Aperture /></span><strong>拾光册</strong></a><p>Made for moments worth keeping.</p><span>© {new Date().getFullYear()}</span></footer>

      {lightbox && <Lightbox item={lightbox} onClose={() => setLightbox(null)} />}
      {adminOpen && admin && <AdminPanel onChanged={loadItems} onLogout={logout} onClose={() => setAdminOpen(false)} pushToast={pushToast} />}
      {loginOpen && (
        <div className="login-backdrop" onClick={() => setLoginOpen(false)}>
          <form className="login-card" onSubmit={login} onClick={(event) => event.stopPropagation()}>
            <button type="button" className="modal-x" onClick={() => setLoginOpen(false)} aria-label="关闭"><X /></button>
            <span className="login-icon"><KeyRound /></span>
            <small>OWNER ACCESS</small>
            <h2>回到你的收藏室</h2>
            <p>{configured ? "输入管理员口令后，可以上传与解析新内容。" : "先在 Cloudflare Pages 中配置 ADMIN_PASSWORD 与 SESSION_SECRET。"}</p>
            <input type="text" name="username" value="admin" autoComplete="username" hidden readOnly />
            <label><span>管理员口令</span><input type="password" name="password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus autoComplete="current-password" placeholder="••••••••••••" /></label>
            <button className="login-submit" disabled={loginBusy || !configured}>{loginBusy ? <LoaderCircle className="spin" /> : <Check />} 进入管理模式</button>
          </form>
        </div>
      )}
      {toast && <div className={`toast ${toast.tone}`}><span>{toast.tone === "success" ? <Check /> : <X />}</span>{toast.message}</div>}
      <nav className="mobile-dock" aria-label="快捷操作">
        <a href="#gallery"><Images /><span>图库</span></a>
        <button className="dock-primary" onClick={openAdmin}><Plus /><span>上传</span></button>
        <button onClick={saveToAlbum} disabled={savingAlbum || !filteredItems.length}>{savingAlbum ? <LoaderCircle className="spin" /> : <Download />}<span>存相册</span></button>
      </nav>
    </div>
  );
}
